// API Configuration - Environment-based with fallbacks
// Development: Uses localhost:8080 for direct backend access
// Production: Uses relative URLs for Nginx proxy

import { DEV_CONFIG, isDevEnvironment } from './dev-config';
import { PROD_CONFIG, isProdEnvironment } from './prod-config';

// Environment detection with multiple fallbacks
const isDevelopment = isDevEnvironment();
const isProduction = isProdEnvironment();

// Set base URL based on environment and ensure no trailing spaces
// Priority: 1. Environment variable, 2. Production config, 3. Development config, 4. Auto-detection, 5. Default
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 
                     (isProduction ? PROD_CONFIG.API_BASE_URL : 
                      isDevelopment ? DEV_CONFIG.API_BASE_URL : '')).trim();

// Helper function to build clean URLs without spaces
const buildUrl = (baseUrl, endpoint) => {
  const cleanBase = baseUrl.trim();
  const cleanEndpoint = endpoint.trim();
  return `${cleanBase}${cleanEndpoint}`;
};

// API endpoints
export const API_ENDPOINTS = {
  // Invoice endpoints
  invoices: buildUrl(API_BASE_URL, '/api/invoices'),
  invoiceById: (id) => buildUrl(API_BASE_URL, `/api/invoices/${id}`),
  invoicePrint: (id) => buildUrl(API_BASE_URL, `/api/invoices/${id}/print`),
  invoicePdf: (id) => buildUrl(API_BASE_URL, `/api/invoices/${id}/pdf`),
  invoiceEmail: (id) => buildUrl(API_BASE_URL, `/api/invoices/${id}/email`),
  invoiceStatus: (id) => buildUrl(API_BASE_URL, `/api/invoices/${id}/status`),
  invoiceDashboard: (period) => buildUrl(API_BASE_URL, `/api/invoices/dashboard/sales?period=${period}`),
  invoicesByCustomer: (customerId) => buildUrl(API_BASE_URL, `/api/invoices/by-customer?customerId=${customerId}`),
  repairItemsSearch: (query) => buildUrl(API_BASE_URL, `/api/invoices/repair-items/search?q=${encodeURIComponent(query)}`),
  
  // Customer endpoints
  customers: buildUrl(API_BASE_URL, '/api/customers'),
  customerSearch: (phone) => buildUrl(API_BASE_URL, `/api/customers/search?phone=${encodeURIComponent(phone)}`),
  
  // Vehicle endpoints
  vehicles: buildUrl(API_BASE_URL, '/api/vehicles'),
  
  // Health endpoints
  health: buildUrl(API_BASE_URL, '/health'),
  healthApi: buildUrl(API_BASE_URL, '/health/api')
};

// Helper function to build API URLs
export const buildApiUrl = (endpoint) => {
  return buildUrl(API_BASE_URL, endpoint);
};

// Helper function to get full API URL for specific endpoints
export const getApiUrl = (endpoint) => {
  return buildUrl(API_BASE_URL, endpoint);
};

// Environment info for debugging
export const API_CONFIG = {
  isDevelopment,
  isProduction,
  baseUrl: API_BASE_URL,
  environment: process.env.NODE_ENV || 'unknown',
  hostname: window.location.hostname,
  port: window.location.port,
  devConfig: isDevelopment ? DEV_CONFIG : null,
  prodConfig: isProduction ? PROD_CONFIG : null
};

// Log configuration in development
if (isDevelopment) {
  console.log('🔧 API Configuration:', API_CONFIG);
  console.log('🌐 API Base URL:', `"${API_BASE_URL}"`); // Show quotes to reveal any hidden spaces
  console.log('⚙️  Development Config:', DEV_CONFIG);
  console.log('🔗 Sample URLs:');
  console.log('  - Customers:', API_ENDPOINTS.customers);
  console.log('  - Invoices:', API_ENDPOINTS.invoices);
}

// Log configuration in production (minimal)
if (isProduction) {
  console.log('🚀 Production Mode - API Base URL:', `"${API_BASE_URL}"`);
  console.log('🌐 Server:', PROD_CONFIG.SERVER_NAME);
}

export default API_ENDPOINTS;
